import * as actions from "../../actions/actions";
import { connect } from "react-redux";
import React from "react";
import classnames from "classnames";
import StripeSplitForm from "./stripe-split-form";
import map from "lodash/map";
import get from "lodash/get";
import isFunction from "lodash/isFunction";
import PropTypes from "prop-types";
import { licensesDisplay, licenses } from "../../reducers/download";

const defaultThankYouTitle = licensesDisplay[licenses.NOUN_PRO].thankYouTitle;
const defaultThankYouText =
    "Enjoy unlimited access to 2 million icons from TheNounProject.com or from any of our Apps or Plugins.";

class StripeUpgradeForm extends React.Component {
    static propTypes = {
        planKey: (props, propName, componentName) => {
            if (!props.submitOverride && !props[propName]) {
                return new Error(
                    "Invalid prop `" +
                        propName +
                        "` supplied to" +
                        " `" +
                        componentName +
                        "`. Provide a planKey or a submitOverride."
                );
            }
        },
        submit: PropTypes.func.isRequired,
        stripeUpdateBillingInfo: PropTypes.func.isRequired,
        submitOverride: PropTypes.func,
        buttonText: PropTypes.string,
        disabled: PropTypes.bool
    };

    static defaultProps = {
        buttonText: "Charge card",
        // Thank you screen enabled with the showThankYou prop
        thankYouTitle: defaultThankYouTitle,
        thankYouText: defaultThankYouText
    };
    state = {};
    shouldShowThankYou() {
        return this.props.showThankYou;
    }

    handleCardComplete = (stripeApi, tokenData) => {
        this.setState({
            stripeApi,
            tokenData
        });
    };

    clearState = () =>
        this.setState({
            stripeApi: null,
            tokenData: null
        });

    attemptSubmitForm = e => {
        e.preventDefault();
        if (this.state.success) {
            if (this.props.onSuccess) {
                return this.props.onSuccess();
            } else {
                throw new Error(
                    "StripeUpgradeForm preventing double submit after success"
                );
            }
        }

        if (this.props.submitOverride) {
            let submitted = this.props.submitOverride(
                this.state.stripeApi,
                this.state.tokenData
            );

            if (this.shouldShowThankYou() && this.props.onSuccess) {
                if (submitted && submitted.then) {
                    submitted = submitted.then(this.handleSubmitted);
                } else {
                    console.warn(
                        "StripeUpgradeForm: submitOverride does not return promise, cant show thank you screen"
                    );
                }
            }

            return submitted;
        }

        let { user } = this.props;
        this.props
            .submit(
                this.state.stripeApi,
                user,
                this.state.tokenData,
                this.props.planKey
            )
            .then(this.handleSubmitted);
    };

    handleSubmitted = error => {
        if (!error) {
            if (this.shouldShowThankYou() && !this.state.thankYou) {
                this.setState({
                    thankYou: true
                });
                this.props.pageview(
                    `/accounts/upgrade/thank-you/${this.props.planKey}/`
                );
                return;
            }
            if (this.props.onSuccess) {
                this.props.onSuccess();
            }
        }
    };

    componentWillReceiveProps(nextProps) {
        if (this.props.user && this.props.user.permissions) {
            let wasPro =
                get(this.props, "user.permissions.market") == "premium";
            let becamePro =
                get(nextProps, "user.permissions.market") == "premium";

            if (!wasPro && becamePro) {
                // Once the user has been upgraded, remove the stripe information
                this.clearState();
                this.setState({
                    success: true
                });
            }
        }
    }

    render() {
        let { success, thankYou } = this.state;
        let isDefaultState = !this.props.error && !success;
        let submitButtonClasses = classnames(
            "button",
            "full-width",
            "button-process",
            {
                blue: isDefaultState,
                "ui_arrow-right-line": isDefaultState,
                processing: this.props.processing,
                "error on red": this.props.error,
                green: this.state.success,
                ui_checkmark: this.state.success,
                disabled:
                    !this.props.error &&
                    !this.props.processing &&
                    this.props.disabled
            }
        );

        let form, onClick, billingInfo, buttonText, thankYouDisplay;

        if (!thankYou) {
            form = this.props.stripePublishableKey && (
                <FormWrapper
                    disabled={this.props.disabled}
                    stripePublishableKey={this.props.stripePublishableKey}
                    handleCardComplete={this.handleCardComplete}
                    clearState={this.clearState}
                />
            );

            onClick = this.props.disabled
                ? e => e.preventDefault()
                : this.attemptSubmitForm;

            buttonText = this.state.success ? "Success" : this.props.buttonText;
            billingInfo = (
                <React.Fragment>
                    <textarea
                        id="receipt-info"
                        className="optional"
                        placeholder="This information shows on your invoices, some people need this for tax purposes. (VAT, Address, Job Number, Etc)."
                        value={this.props.billingInfo}
                        disabled={this.props.disabled || processing}
                        onChange={e =>
                            this.props.stripeUpdateBillingInfo(e.target.value)
                        }
                    />
                    <label
                        htmlFor="receipt-info"
                        data-label-text="Optional Billing Information"
                    >
                        Optional Billing Information <em>( optional )</em>
                    </label>
                    <em>( optional )</em>
                </React.Fragment>
            );
        } else {
            let thankYouText = this.props.thankYouText;
            if (isFunction(thankYouText)) {
                thankYouText = thankYouText();
            }

            thankYouDisplay = (
                <React.Fragment>
                    {this.props.thankYouTitle ? (
                        <h1 className="font-xlarge">
                            {this.props.thankYouTitle}
                        </h1>
                    ) : null}
                    {thankYouText ? (
                        <p className="font-medium">{thankYouText}</p>
                    ) : null}
                </React.Fragment>
            );

            onClick = this.handleSubmitted.bind(this, null);
            buttonText = "Continue";
        }

        let { error, processing } = this.props;

        error = <div className="error on stripe-error">{error}</div>;

        let submitButton = (
            <div
                className={submitButtonClasses}
                type="submit"
                onClick={onClick}
                data-test="stripe-form-submit-button"
            >
                <span>{buttonText}</span>
            </div>
        );

        if (!thankYouDisplay) {
            return this.props.children({
                form,
                submitButton,
                billingInfo,
                error
            });
        } else {
            return (
                <div className="upgrade-thank-you-wrapper">
                    {thankYouDisplay}

                    <div className="margin-t-l">
                        {submitButton}
                        {error}
                    </div>
                </div>
            );
        }
    }
}

// Tracks unmounting of Stripe Elements, since StripeUpgradeForm depends on its
// children to mount the actual form, it needs to know when the Stripe elements have
// been unmounted so it can discard its saved reference to those elements' stripeApi
class FormWrapper extends React.Component {
    componentWillUnmount() {
        if (this.props.clearState) {
            this.props.clearState();
        }
    }
    render() {
        return (
            <StripeSplitForm
                disabled={this.props.disabled}
                stripePublishableKey={this.props.stripePublishableKey}
                handleComplete={this.props.handleCardComplete}
            />
        );
    }
}

function mapStateToProps(state, ownProps) {
    let { stripe, user } = state;
    let { stripe: stripePublishableKey } = user;
    let { processing, error, fieldErrors, billingInfo } = stripe;

    // Don't show the general error if the same message is being shown by a field
    if (error && map(fieldErrors, (err, field) => err).includes(error)) {
        error = null;
    }

    billingInfo = !billingInfo ? "" : billingInfo;

    return {
        user,
        processing,
        error,
        stripePublishableKey,
        billingInfo
    };
}

export { StripeUpgradeForm as UnwrappedForm, mapStateToProps };

export default connect(
    mapStateToProps,
    {
        submit: actions.doUpgrade,
        stripeUpdateBillingInfo: actions.stripeUpdateBillingInfo,
        pageview: actions.pageview
    }
)(StripeUpgradeForm);
